import { useState, useEffect, FormEvent, useCallback } from 'react';
import { Select, InputLabel, MenuItem, FormControl, TextField, Grid, Box } from '@mui/material';
import { toast } from '@cincoders/cinnamon';
import {
  ProcessDTO,
  ParametersValueDTO,
  TypeProcessValueDTO,
  ResponseProcess,
  ProcessParameters,
  ProcessEditDTO,
} from '../../types/Process.d';
import { PageForm, FormButton } from '../../components/FormLayout/FormLayout.styles';
import { ProcessTypeService } from '../../services/ProcessTypeService';
import { ProcessService } from '../../services/ProcessService';
import useDebounce from '../../hooks/useDebounce';
import { ProcessStatus } from '../../types/enums';
import { DivArchive } from '../../components/Archive/index';
import { ArchiveButton, DivProcessStatus, ProcessDescription, ProcessTitle, Span } from './styles';
import ProcessesParametersList from '../../components/ProcessParameters';
import Manual from '../../components/Manual';

const initialTypeProcess: TypeProcessValueDTO = {
  id: 0,
  name: '',
  averageTime: 0,
  timeLimit: 0,
  parameters: [],
};
const initialProcess: ProcessDTO = {
  id: 0,
  name: '',
  number: '',
  status: 0,
  favourite: false,
  type: 0,
  parameters: [],
};

export default function FormProcess(): JSX.Element {
  const [typeProcessList, setTypeProcessList] = useState<TypeProcessValueDTO[]>([]);
  const [typeProcess, setTypeProcess] = useState<TypeProcessValueDTO>(initialTypeProcess);
  const [process, setProcess] = useState<ProcessDTO>(initialProcess);
  const [loading, setLoading] = useState<boolean>(false);
  const debouncedNumber = useDebounce(process.number, 2000);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  async function getProcessesType() {
    const response = await ProcessTypeService.getTypeProcesses();
    if (response.status === 200) {
      setTypeProcessList(response.data);
    }
  }

  async function createProcess(processEdit: ProcessEditDTO) {
    const response = await ProcessService.createProcess(processEdit);
    if (response.status === 201) {
      toast.success('Processo criado com sucesso!');
    }
  }

  async function editProcess(processEdit: ProcessEditDTO) {
    const response = await ProcessService.editProcess(processEdit);
    if (response.status === 200) {
      toast.success('Processo editado com sucesso!');
    } else {
      toast.success('Erro ao editar processo.');
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const processParametersList: ProcessParameters[] = [];
    process.parameters.forEach(parameters => {
      if (parameters.value) {
        const processParametersTemp: ProcessParameters = {
          parameterId: parameters.id,
          value: parameters.value,
        };
        processParametersList.push(processParametersTemp);
      }
    });
    const processEdit: ProcessEditDTO = {
      id: process.id,
      name: process.name,
      number: process.number,
      type: process.type,
      parameters: processParametersList,
    };
    if (isEditing) {
      editProcess(processEdit);
    } else {
      createProcess(processEdit);
    }
  }
  async function onArchiveProcessClick() {
    setLoading(true);
    if (process.id && process.status !== 2) {
      try {
        const response = await ProcessService.editProcessStatus(process.id, ProcessStatus.ARCHIVE);
        if (response.status === 200) {
          toast.success('Processo arquivado com sucesso!');
          setProcess({ ...process, status: 2 });
        }
      } catch {
        toast.error('Erro ao arquivar o processo. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    } else if (process.id && process.status === 2) {
      try {
        const response = await ProcessService.editProcessStatus(process.id, ProcessStatus.PENDING);
        if (response.status === 200) {
          toast.success('Processo desarquivado com sucesso!');
          setProcess({ ...process, status: 4 });
        }
      } catch {
        toast.error('Erro ao desarquivar o processo. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    }
  }

  const changeProcessParams = useCallback(
    (responseData: ResponseProcess | undefined) => {
      const parametersList: ParametersValueDTO[] = [];
      let newName = '';
      if (responseData) {
        newName = responseData?.name;
        if (responseData.parameters) {
          responseData.parameters.forEach(parameters => {
            parametersList.push(parameters);
          });
        }
        setTypeProcess(responseData.typeProcess);
        setProcess({
          ...process,
          id: responseData.id,
          name: newName,
          number: responseData.number,
          status: responseData.status,
          favourite: responseData.favourite,
          type: responseData.typeProcess.id,
          parameters: parametersList,
        });
      }
    },
    [process],
  );

  const changeParamsList = useCallback(
    (typeProcessSelect: TypeProcessValueDTO) => {
      setTypeProcess(typeProcessSelect);
      typeProcessSelect.parameters.forEach(parameter => {
        process.parameters.push({
          id: parameter.id,
          value: parameter.value,
          type: parameter.type,
          abbreviation: parameter.abbreviation,
          description: parameter.description,
          typeProcessId: parameter.typeProcessId,
        });
      });
      setProcess({
        ...process,
        type: typeProcessSelect.id,
      });
    },
    [process],
  );

  useEffect(() => {
    getProcessesType();
  }, []);

  async function findProcess(name: string | undefined, number: string | undefined) {
    const response = await ProcessService.findProcess(name, number);
    if (response.status === 200) {
      setIsEditing(true);
      changeProcessParams(response.data);
    } else if (response.status === 404) {
      setIsEditing(false);
    }
  }

  useEffect(() => {
    if (debouncedNumber !== '') {
      findProcess(undefined, debouncedNumber);
    } else {
      setProcess(initialProcess);
      setIsEditing(false);
    }
  }, [debouncedNumber]);

  const processTypeName = typeProcessList.map(processType => (
    <MenuItem key={processType.id} value={processType.name}>
      {processType.name}
    </MenuItem>
  ));

  function onChangeParam(id: number, targetValue: string) {
    const newParameters = [...process.parameters];
    for (let i = 0; i < newParameters.length; i += 1) {
      if (id === newParameters[i].id) {
        newParameters[i].value = targetValue;
        setProcess({ ...process, parameters: newParameters });
      }
    }
  }

  const typeProcessParameters = process.parameters.map(parameter => {
    let newValue = '';
    process.parameters.forEach(parameters => {
      if (parameters.id === parameter.id) {
        newValue = parameters.value;
      }
    });
    if (!newValue) {
      newValue = '';
    }
    return (
      <ProcessesParametersList
        parameter={parameter}
        newValue={newValue}
        onChange={changedValue => onChangeParam(parameter.id, changedValue)}
      />
    );
  });

  function getProcessDescription(status: number): string {
    switch (status) {
      case ProcessStatus.ACTIVE:
        return 'Ativo';
      case ProcessStatus.ARCHIVE:
        return 'Arquivado';
      case ProcessStatus.CONCLUDED:
        return 'Concluído';
      case ProcessStatus.INVALID:
        return 'Inválido';
      case ProcessStatus.PENDING:
        return 'Pendente';
      default:
        return 'Processo não existe';
    }
  }

  return (
    <>
      <Manual
        visible={false}
        manualTitle='Manual SIPAC'
        itemsManual={[
          {
            itemTitle: 'Cadastrar Processo',
            itemBody: 'Para cadastrar um processo é necessário preencher os campos “N° de processo” e “Nome”',
          },
          {
            itemTitle: 'Parâmetros',
            itemBody:
              'As opções de parâmetros ficarão disponíveis de acordo com a opção “Tipo Processo” selecionado. ' +
              'Para conclusão do cadastro, basta clicar no botão salvar',
          },
          {
            itemTitle: 'Arquivamento',
            itemBody:
              'Arquivamento do processo é realizado clicando no ícone em formato de pasta ao lado do ' +
              'status do processo',
          },
        ]}
      />
      <PageForm>
        <h1>{isEditing ? 'Edição de Processo' : 'Cadastro de Processo'}</h1>
        <main>
          <form onSubmit={e => handleSubmit(e)}>
            <Grid container spacing={5} paddingTop={2} marginBottom={2}>
              <Grid item xs={6}>
                <TextField
                  label='N° do processo'
                  id='IdProcesso'
                  InputProps={{ inputProps: { maxLength: 20 } }}
                  value={process.number}
                  onChange={e => {
                    const validate = /^[0-9-.'-''/']*$/g;
                    if (validate.test(e.target.value) && process.number !== e.target.value) {
                      setProcess({
                        ...initialProcess,
                        number: e.target.value,
                      });
                      setTypeProcess(initialTypeProcess);
                    }
                  }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label='Nome'
                  value={process.name}
                  fullWidth
                  id='Name'
                  onChange={e => {
                    setProcess({ ...process, name: e.target.value });
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo Processo</InputLabel>
                  <Select
                    label='Tipo processo'
                    name='type'
                    id='Tipo do Processo'
                    value={typeProcess.name}
                    onChange={e => {
                      typeProcessList.forEach(parameters => {
                        if (e.target.value === parameters.name) {
                          process.parameters = [];
                          changeParamsList(parameters);
                        }
                      });
                    }}
                    fullWidth
                  >
                    {processTypeName}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                {isEditing && (
                  <DivProcessStatus>
                    <Span>
                      <ProcessTitle>Status do processo</ProcessTitle>
                      <ProcessDescription>{getProcessDescription(process.status)}</ProcessDescription>
                    </Span>
                    <ArchiveButton onClick={onArchiveProcessClick}>
                      <DivArchive status={process.status} loading={loading} />
                    </ArchiveButton>
                  </DivProcessStatus>
                )}
              </Grid>
            </Grid>
            <h3>Parâmetros</h3>
            <Box sx={{ display: 'grid', gap: 2 }}>{typeProcessParameters}</Box>
            <hr />
            <FormButton variant='contained' type='submit'>
              Salvar
            </FormButton>
          </form>
        </main>
      </PageForm>
    </>
  );
}
